import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MessageCircle, MapPin } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { FOOTER } from "@/constants/testIds";
import { API_URL } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, ok: false, err: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ loading: true, ok: false, err: "" });
    try {
      await axios.post(`${API_URL}/newsletter`, { email, source: "footer" });
      setState({ loading: false, ok: true, err: "" });
      setEmail("");
      setTimeout(() => setState((s) => ({ ...s, ok: false })), 4000);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Could not subscribe right now.";
      setState({ loading: false, ok: false, err: String(msg) });
      setTimeout(() => setState((s) => ({ ...s, err: "" })), 4000);
    }
  };

  return (
    <footer className="relative border-t border-white/5 bg-[#050505] mt-32 overflow-hidden">
      <div className="px-6 md:px-12 pt-20 pb-10 select-none">
        <h2
          className="font-cabinet font-bold uppercase leading-[0.88] tracking-[-0.045em] text-white"
          style={{ fontSize: "clamp(2.75rem, 9vw, 8.5rem)" }}
        >
          Theme<span className="text-[#e03c31]">.</span>
          <br />
          Studios
        </h2>
      </div>

      <div className="px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 pb-16">
        <div className="md:col-span-5">
          <p className="text-xs uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
            Newsletter
          </p>
          <h3 className="font-cabinet text-xl md:text-2xl font-bold leading-snug mb-5">
            Get studio drops, behind-the-scenes & monthly drop notifications.
          </h3>
          <form onSubmit={onSubmit} className="flex items-center gap-2 max-w-md">
            <input
              data-testid={FOOTER.newsletter}
              type="email"
              required
              placeholder="you@yourbrand.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-zinc-500 focus:outline-none focus:border-[#e03c31]/60 transition-colors"
            />
            <button
              data-testid={FOOTER.newsletterSubmit}
              type="submit"
              disabled={state.loading}
              className="btn-magnetic !py-3 !px-5 disabled:opacity-60"
            >
              <ArrowUpRight size={16} />
            </button>
          </form>
          {state.ok && (
            <p className="mt-3 text-sm text-emerald-400" data-testid="newsletter-success">
              Subscribed. We&apos;ll be in touch.
            </p>
          )}
          {state.err && (
            <p className="mt-3 text-sm text-red-400" data-testid="newsletter-error">{state.err}</p>
          )}
        </div>

        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-bold mb-4">
            Studio
          </p>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li><Link to="/about" className="hover:text-[#e03c31] transition-colors">About</Link></li>
            <li><Link to="/services" className="hover:text-[#e03c31] transition-colors">Services</Link></li>
            <li><Link to="/portfolio" className="hover:text-[#e03c31] transition-colors">Work</Link></li>
            <li><Link to="/contact" className="hover:text-[#e03c31] transition-colors">Contact</Link></li>
          </ul>
          <p className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-bold mt-7 mb-4">
            Service Lanes
          </p>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li><Link to="/services/posters-social" className="hover:text-[#e03c31] transition-colors">Posters & Social</Link></li>
            <li><Link to="/services/reels-motion" className="hover:text-[#e03c31] transition-colors">Reels & Motion</Link></li>
            <li><Link to="/services/branding-identity" className="hover:text-[#e03c31] transition-colors">Branding & Identity</Link></li>
            <li><Link to="/services/ads-profile" className="hover:text-[#e03c31] transition-colors">Ads & Profile</Link></li>
            <li><Link to="/services/monthly-systems" className="hover:text-[#e03c31] transition-colors">Monthly Systems</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-bold mb-4">
            Get in touch
          </p>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3 text-zinc-300">
              <MapPin size={16} className="mt-0.5 text-[#e03c31]" />
              <span>Remote studio · Madurai, Tamil Nadu<br />Serving brands across India & beyond</span>
            </li>
            <li className="flex items-center gap-3 text-zinc-300">
              <MessageCircle size={16} className="text-[#e03c31]" />
              <a href="https://wa.me/918903205676" target="_blank" rel="noreferrer" className="hover:text-white">
                WhatsApp · +91 8903 205 676
              </a>
            </li>
            <li className="flex items-center gap-3 text-zinc-300">
              <Mail size={16} className="text-[#e03c31]" />
              <a href="mailto:support@themestudios.in" className="hover:text-white">support@themestudios.in</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="px-6 md:px-12 pb-10 border-t border-white/5 pt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl">
          <div className="flex items-start gap-2 text-xs text-zinc-500">
            <span className="text-[#e03c31] mt-0.5">✱</span>
            <span>ThemeStudios never asks for card details in WhatsApp chat.</span>
          </div>
          <div className="flex items-start gap-2 text-xs text-zinc-500">
            <span className="text-[#e03c31] mt-0.5">✱</span>
            <span>Payments are requested only after package and scope confirmation.</span>
          </div>
          <div className="flex items-start gap-2 text-xs text-zinc-500">
            <span className="text-[#e03c31] mt-0.5">✱</span>
            <span>Official support happens through the listed WhatsApp number and support email.</span>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 py-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-zinc-500">
        <p>
          © {new Date().getFullYear()} ThemeStudios Digital Designers · Remote design studio,
          Madurai · Original layouts · Copyright-safe briefs
        </p>
        <div className="flex items-center gap-5">
          <Link to="/privacy-policy" className="hover:text-white">Privacy</Link>
          <Link to="/terms-and-conditions" className="hover:text-white">Terms</Link>
          <Link to="/refund-policy" className="hover:text-white">Refunds</Link>
        </div>
      </div>
    </footer>
  );
}
